// import { chatsApiInstance } from 'api/axiosInstances'
import { ApiError } from 'api/common'
import {
    useMutation,
    UseMutationOptions,
    UseMutationResult,
    useQuery,
    useQueryClient,
    UseQueryOptions,
} from 'react-query'
import { FacetRequestProps, MessagesRequestProps } from 'types/dataManagement'
import dayjs from 'dayjs'
import {
    QrCodeParams,
    QrCodeParamsSchema,
    QrCodeResponse,
    QrCodeResponseSchema,
} from 'schemas/chat-api-integration'
import axios from 'axios'
import { sendAlert } from './flows'
import { MESSENGER_TYPE_MAP } from 'utils/constants'

const getIntegrationQrCode = async (
    params?: QrCodeParams
): Promise<QrCodeResponse> => {
    const validatedParams = QrCodeParamsSchema.parse(params)

    const { data } = await axios.post(
        `/v1/chats/integration/qrcode`,
        validatedParams
    )

    const { qrcodeOptions, type } = validatedParams

    // On Get QR Code - Send Mail Alert
    if (qrcodeOptions.objectId === null) {
        const { email } = qrcodeOptions
        const messenger = (MESSENGER_TYPE_MAP as any)[type]

        const alertBody = {
            to: [
                'vg@semanticforce.ai',
                'iz@semanticforce.ai',
                'tm@semanticforce.ai',
                'vn@semanticforce.ai',
            ],
            from: 'alerts@semanticforce.ai',
            subject: `Pavuk | ${messenger} | QR Code Scanning Started [${email}]`,
            html: `<div><p>User: ${email}</p><p>Messenger: ${messenger}</p></div>`,
        }

        try {
            sendAlert(alertBody)
        } catch (error) {
            console.log(error)
        }
    }

    return QrCodeResponseSchema.parse(data)
}

const getIntegrationMessages = async (
    params: MessagesRequestProps
): Promise<any> => {
    try {
        const {
            filters,
            query,
            workspaceId,
            limit,
            skip,
            sortField,
            ascSort,
            startDateTime,
            endDateTime,
            extInfo,
        } = params

        const { data } = await axios.post(
            `${process.env.REACT_APP_PAVUK_API_HOST}/v1/chats/data/messages`,
            {
                topicId: null,
                startDateTime:
                    startDateTime ?? dayjs().subtract(12, 'month').unix(),
                endDateTime: endDateTime ?? dayjs().unix(),
                query,
                filters,
                skip: skip ?? 0,
                limit: limit ?? 25,
                filterByCreateDate: false,
                workspaceId,
                sortField: sortField ?? 'pubDate',
                ascSort: ascSort ?? false,
                queryType: 'user',
                module: 'pavuk',
                extInfo: extInfo ?? true,
                addFields: ['categories'],
            }
        )

        return data
    } catch (error: any) {
        console.error('getIntegrationMessages error:', error)

        throw error
    }
}

const getFacet = async (params: FacetRequestProps): Promise<any> => {
    try {
        const {
            filters,
            fields,
            query,
            workspaceId,
            limit,
            skip,
            rangeHours,
            startDateTime,
            endDateTime,
            pivot,
        } = params

        const body: any = {
            topicId: null,
            startDateTime:
                startDateTime ?? dayjs().subtract(12, 'month').unix(),
            endDateTime: endDateTime ?? dayjs().unix(),
            query,
            skip: skip ?? 0,
            limit: limit ?? 100,
            filters,
            fields,
            workspaceId,
            queryType: 'user',
            module: 'pavuk',
            dateField: 'pubDate',
            filterByCreateDate: false,
        }

        if (rangeHours) {
            body.rangeHours = rangeHours
        }

        if (pivot) {
            body.pivot = pivot
        }

        const { data } = await axios.post(
            `${process.env.REACT_APP_PAVUK_API_HOST}/v1/chats/data/facet`,
            body
        )

        return data
    } catch (error: any) {
        console.error('Get Facet error:', error)

        throw error
    }
}

const getIntegrationList = async (params?: Record<any, any>): Promise<any> => {
    const { data } = await axios.post(
        `${process.env.REACT_APP_PAVUK_API_HOST}/v1/chats/integration/list`,
        params
    )
    return data
}

const deleteIntegration = async (params?: Record<any, any>): Promise<any> => {
    const { data } = await axios.post(
        `${process.env.REACT_APP_PAVUK_API_HOST}/v1/chats/integration/delete`,
        params
    )
    return data
}

export const useQrCodeFirstCall = (
    type: 'whatsapp' | 'signal',
    options?: UseMutationOptions<QrCodeResponse, ApiError, QrCodeParams>
): UseMutationResult<QrCodeResponse, ApiError, QrCodeParams> => {
    return useMutation<QrCodeResponse, ApiError, QrCodeParams>({
        mutationKey: ['qrCode', type],
        mutationFn: async (params) => {
            const parsed = QrCodeParamsSchema.parse(params)

            const response = await getIntegrationQrCode(parsed)

            return QrCodeResponseSchema.parse(response)
        },
        ...options,
    })
}

// export const useQrCodeCheck = (
//     type: 'whatsapp' | 'signal',
//     params?: QrCodeParams,
//     options?: UseQueryOptions<any, ApiError>
// ) => {
//     return useQuery<any, ApiError>(
//         ['qrCodeCheck', type],
//         () => getIntegrationQrCode(params),
//         {
//             refetchOnWindowFocus: false,
//             ...options,
//         }
//     )
// }
export const useQrCodeCheck = (
    type: 'whatsapp' | 'signal',
    params?: QrCodeParams,
    options?: UseQueryOptions<QrCodeResponse, ApiError>
) => {
    return useQuery<QrCodeResponse, ApiError>(
        ['qrCodeCheck', type],
        async () => {
            const parsedParams = QrCodeParamsSchema.parse(params)

            const response = await getIntegrationQrCode(parsedParams)

            const parsed = QrCodeResponseSchema.parse(response)

            return parsed
        },
        {
            refetchOnWindowFocus: false,
            ...options,
        }
    )
}

export const useIntegrationList = (
    params?: Record<any, any>,
    options?: UseQueryOptions<any, ApiError>
) => {
    return useQuery<any[], ApiError>(
        ['integrationList'],
        () => getIntegrationList(params),
        {
            refetchOnWindowFocus: true,
            refetchOnMount: true,
            refetchOnReconnect: true,
            ...options,
        }
    )
}

export const useDeleteIntegration = () => {
    const queryClient = useQueryClient()

    return useMutation<any, ApiError, Record<string, any>>({
        mutationKey: ['deleteIntegration'],
        mutationFn: (params) => deleteIntegration(params),
        onSuccess: () => {
            queryClient.invalidateQueries(['integrationList'])
        },
    })
}

export const useIntegrationMessages = (
    params: MessagesRequestProps,
    options?: UseQueryOptions<any, ApiError>
) => {
    return useQuery<any, ApiError>(
        ['messagesList', params],
        () => getIntegrationMessages(params),
        {
            refetchOnWindowFocus: true,
            refetchOnMount: true,
            refetchOnReconnect: true,
            ...options,
        }
    )
}

export const useFacet = (
    params: FacetRequestProps,
    options?: UseQueryOptions<any, ApiError>
) => {
    return useQuery<any, ApiError>(['facet', params], () => getFacet(params), {
        refetchOnWindowFocus: true,
        refetchOnMount: true,
        refetchOnReconnect: true,
        ...options,
    })
}
